import { useMemo, useState } from 'react';
import { FilterObject } from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/utils/types';
import { useMount } from 'react-use-v13';

export type TableFilteringContext = {
  exactFieldOptionsMap: Record<string, string[] | { id: string; name: string }[]>;
  fieldOptions: { id: string; name: string }[];
  fieldMap: Record<string, 'number' | 'fuzzy' | 'exact' | 'in_only' | 'is_only' | 'date'>;
  filteredData: any[];
  filters: FilterObject;
  setFilters: (filters: FilterObject) => void;
};

export const DEFAULT_FILTER = {
  operator: 'and',
  filters: [{ field: undefined, option: undefined, value: undefined }],
};

export const useTableFilteringContext = (props: {
  key?: string;
  data: any[];
  fieldFilters: {
    [key: string]: {
      name: string;
      type: 'number' | 'fuzzy' | 'exact' | 'in_only' | 'is_only' | 'date';
    };
  };
  exactFieldOptionsMap?: Record<string, string[] | { id: string; name: string }[]>;
}): TableFilteringContext => {
  const { data, fieldFilters, exactFieldOptionsMap, key } = props;
  const [filters, setLocalFilters] = useState<FilterObject>(
    key && localStorage.getItem(key) ? JSON.parse(localStorage.getItem(key)!) : DEFAULT_FILTER,
  );
  const [fieldOptions, setFieldOptions] = useState<{ id: string; name: string }[]>([]);

  const setFilters = (filters: FilterObject) => {
    setLocalFilters(filters);
    if (key) {
      localStorage.setItem(key, JSON.stringify(filters));
    }
  };

  useMount(() => {
    setFieldOptions(
      Object.entries(fieldFilters).map(entry => ({ id: entry[0], name: entry[1].name })),
    );
  });

  const fieldMap = useMemo(
    () => Object.fromEntries(Object.entries(fieldFilters).map(entry => [entry[0], entry[1].type])),
    [fieldFilters],
  );

  const filteredData = useMemo(() => {
    const o = filters.operator;
    const f = filters.filters;
    return (data ?? []).filter((item: any) => {
      const filterResults: boolean[] = f.map(filter => {
        const field = filter.field;
        const option = filter.option;
        const value = typeof filter.value === 'string' ? filter.value.toLowerCase() : filter.value;
        if (!field || !option || !value) return true;
        const fieldValue =
          typeof item[field] === 'string' ? item[field].toLowerCase() : item[field];

        switch (option) {
          case 'is':
            return Array.isArray(fieldValue) ? fieldValue.includes(value) : fieldValue === value;
          case 'isNot':
            return fieldValue !== value;
          case 'contains':
            return fieldValue.includes(value);
          case 'doesNotContain':
            return !fieldValue.includes(value);
          case 'gt':
            return +fieldValue > +value;
          case 'lt':
            return +fieldValue < +value;
          case 'eq':
            return +fieldValue === +value;
          default:
            return false;
        }
      });
      if (o === 'and') {
        return filterResults.every(result => result);
      } else {
        return filterResults.some(result => result);
      }
    });
  }, [filters.filters, filters.operator, data]);

  return {
    exactFieldOptionsMap: exactFieldOptionsMap ?? {},
    fieldOptions,
    fieldMap,
    filteredData,
    filters,
    setFilters,
  };
};
